import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ASSETS } from "@/lib/assets";
import {
  Skull, ChevronRight, ChevronLeft, Swords, Users, Map as MapIcon,
  Crown, Trophy, Target, Flag, Zap, ArrowUp, CheckCircle2,
} from "lucide-react";

/**
 * Tutorial — shown to any new player whose `tutorial_seen` is false.
 * Also reachable from Command page "HELP" link.
 *
 * Step-based: one concept per screen. Reads cleanly, paces the onboarding.
 */

const STEPS = [
  {
    id: "aim",
    icon: Skull,
    label: "// CHAPTER I",
    title: "YOU ARE THE DEAD",
    body: [
      "You start as a grunt — a hungry corpse at the bottom of the horde. You have no weapons. The humans do.",
      "Every human you tear apart rises beside you. Every zombie lost is gone forever.",
      "Your goal: climb the ladder from Grunt to Emperor of the Dead. Follow orders. Then break them. Then rule.",
    ],
  },
  {
    id: "battle",
    icon: Swords,
    label: "// CHAPTER II",
    title: "HOW BATTLES WORK",
    body: [
      "Each battle asks you three questions: approach, target, tactic.",
    ],
    cards: [
      {
        icon: MapIcon,
        heading: "APPROACH",
        copy: "HOW you enter. A cornfield crawl is slow but hidden. A rooftop drop is loud but lethal. The approach sets your ambush vs. attrition baseline.",
      },
      {
        icon: Target,
        heading: "TARGET",
        copy: "WHO you hit first. Civilians convert easily (75%). Armed soldiers are a coin flip (50%). Officers are nearly useless if killed (25%) but they break the enemy's will.",
      },
      {
        icon: Flag,
        heading: "TACTIC",
        copy: "HOW you fight. Use the mass. Go lone strike. Surround. Send your personal army. Or improvise with 'OTHER' — write your plan, the AI judges it.",
      },
    ],
  },
  {
    id: "permadeath",
    icon: Users,
    label: "// CHAPTER III",
    title: "THE DEAD DO NOT COME BACK",
    body: [
      "Every zombie you commit to battle is one you can lose. Permadeath is absolute — there are no respawns.",
      "Send too many. Lose them all. The humans will wait for you in the next valley with stronger guns.",
      "Send too few. Watch humans slip away. Your horde grows slow. Your superior frowns.",
      "Every battle is a wager: engagement, commitment, and the forces you bring.",
    ],
  },
  {
    id: "ranks",
    icon: ArrowUp,
    label: "// CHAPTER IV",
    title: "CLIMBING THE LADDER",
    body: [
      "There are seven ranks. Each unlocks new influence over the world.",
    ],
    ladder: [
      { name: "Grunt",         req: "You start here.",                      mode: "Personal combat. Boots in the mud." },
      { name: "Squad Leader",  req: "Grow your horde to 51.",               mode: "Still personal duels." },
      { name: "Lieutenant",    req: "Grow your horde to 301.",              mode: "Command-level battles begin." },
      { name: "Warlord",       req: "Grow your horde to 1,001.",            mode: "World map suggestions become influential." },
      { name: "Crown Prince",  req: "Grow your horde to 3,001.",            mode: "You may attempt rebellion." },
      { name: "King of the Dead", req: "Defeat the King at the Bone Throne.", mode: "The world listens to you." },
      { name: "Emperor of the Dead", req: "Control 11 regions on the world map.", mode: "Endgame. The world is yours." },
    ],
  },
  {
    id: "world",
    icon: MapIcon,
    label: "// CHAPTER V",
    title: "THE WORLD MAP",
    body: [
      "At 50 zombies, the world map unlocks. Before Crown Prince, it's a place to SUGGEST — your superior decides if they listen. Your rank + reputation + lenience determines how often they do.",
      "As Crown Prince, you command regions directly. Take enough of them and you qualify to overthrow the King.",
      "The King sits in the Bone Throne — 50,000 hostages, 95% fortification. You'll need a horde and a plan.",
      "Take his throne. Take 11 regions. Become Emperor.",
    ],
  },
  {
    id: "glory",
    icon: Trophy,
    label: "// CHAPTER VI",
    title: "LEADERBOARD & LEGACY",
    body: [
      "Every Monday 00:00 UTC the weekly leaderboard resets. Top three receive horde + reputation rewards scaled to the leader's size and your rank tier.",
      "The Hall of Hordes records the largest hordes EVER assembled. These numbers do not reset. They are etched the moment they were reached. The dead remember.",
    ],
  },
  {
    id: "practice",
    icon: Swords,
    label: "// CHAPTER VII",
    title: "A PRACTICE FIGHT",
    body: [
      "One mock duel. No stakes — no horde lost, no record kept. Pick an approach, a target, and a tactic. See what each choice would do.",
    ],
    practice: true,
  },
  {
    id: "rise",
    icon: Crown,
    label: "// FINAL CHAPTER",
    title: "RISE",
    body: [
      "That's all you need. The rest you'll learn in the mud.",
      "The commander narrates your path live. Every fight is different. Every choice matters.",
      "Stay a grunt. Or climb to Emperor. You decide.",
    ],
  },
];

export default function Tutorial() {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { auth, refreshState } = useAuth();

  const S = STEPS[step];
  const last = step === STEPS.length - 1;
  const Icon = S.icon;

  const finish = async () => {
    setSaving(true);
    try {
      if (auth) {
        await api.post("/game/tutorial/seen");
        await refreshState();
      }
    } catch (e) {
      // non-blocking
    } finally {
      setSaving(false);
      navigate("/command");
    }
  };

  const skip = () => finish();

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Hero backdrop */}
      <div className="absolute inset-0 z-0">
        <img src={ASSETS.hero} alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0"
             style={{ background: "linear-gradient(180deg, rgba(3,3,3,0.7) 0%, rgba(3,3,3,0.95) 50%, rgba(3,3,3,1) 100%)" }} />
      </div>

      {/* Top bar */}
      <nav className="relative z-10 px-6 sm:px-12 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Skull className="w-5 h-5 text-blood" />
          <span className="tac-label" data-testid="tutorial-brand">
            ZOMBIE USURPER // THE INDUCTION
          </span>
        </div>
        <button onClick={skip} className="btn-ghost" data-testid="tutorial-skip-button" disabled={saving}>
          SKIP →
        </button>
      </nav>

      {/* Step indicator */}
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-8 pt-8">
        <div className="flex gap-1.5" data-testid="tutorial-progress">
          {STEPS.map((s, i) => (
            <div key={s.id}
                 className={`h-0.5 flex-1 transition-all ${
                   i === step ? "bg-blood" :
                   i < step ? "bg-bone/60" : "bg-[var(--border-subtle)]"
                 }`}
                 data-testid={`tutorial-progress-${i}`}
            />
          ))}
        </div>
        <p className="tac-label mt-3 text-muted-h">
          {String(step + 1).padStart(2, "0")} / {String(STEPS.length).padStart(2, "0")}
        </p>
      </div>

      {/* Content */}
      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-8 pt-8 pb-24 min-h-[60vh]">
        <div key={S.id} className="fade-up">
          <div className="flex items-center gap-3 mb-5">
            <Icon className="w-4 h-4 text-blood" strokeWidth={1.5} />
            <p className="tac-label" data-testid="tutorial-step-label">{S.label}</p>
          </div>

          <h1 className="font-serif-h text-4xl sm:text-5xl lg:text-6xl uppercase text-bone leading-none mb-8"
              data-testid="tutorial-step-title">
            {S.title}
          </h1>

          <div className="space-y-4 max-w-2xl">
            {S.body.map((p, i) => (
              <p key={i} className="text-base sm:text-lg leading-relaxed text-[var(--text-main)]"
                 data-testid={`tutorial-body-${i}`}>
                {p}
              </p>
            ))}
          </div>

          {/* Battle cards (Chapter II) */}
          {S.cards && (
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="tutorial-battle-cards">
              {S.cards.map((c) => {
                const CIcon = c.icon;
                return (
                  <div key={c.heading} className="narrative-panel"
                       data-testid={`tutorial-battle-card-${c.heading.toLowerCase()}`}>
                    <CIcon className="w-4 h-4 text-blood mb-3" strokeWidth={1.5} />
                    <p className="tac-label mb-3">{c.heading}</p>
                    <p className="text-sm text-[var(--text-main)] leading-relaxed">{c.copy}</p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Rank ladder (Chapter IV) */}
          {S.ladder && (
            <ol className="mt-10 space-y-2" data-testid="tutorial-rank-ladder">
              {S.ladder.map((r, i) => (
                <li key={r.name}
                    className={`flex items-baseline gap-4 px-4 py-3 border-l-2 ${
                      i === 0 ? "border-blood bg-[#1a0606]" :
                      i === S.ladder.length - 1 ? "border-[#5e4a3a]" :
                      "border-[var(--border-subtle)]"
                    }`}
                    data-testid={`tutorial-ladder-row-${i}`}>
                  <span className="font-mono text-[0.7rem] tabular-nums text-muted-h w-6">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif-h text-xl text-bone">{r.name}</p>
                    <p className="text-[0.7rem] tracking-wider text-muted-h mt-1">{r.req}</p>
                  </div>
                  <p className="hidden sm:block text-xs text-muted-h italic max-w-[40%] text-right">
                    {r.mode}
                  </p>
                </li>
              ))}
            </ol>
          )}

          {/* Practice battle (Chapter VII) */}
          {S.practice && <PracticeBattle />}
        </div>

        {/* Nav */}
        <div className="mt-14 flex items-center justify-between gap-3">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0 || saving}
            className="btn-ghost flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="tutorial-back-button"
          >
            <ChevronLeft className="w-3 h-3" /> BACK
          </button>
          {last ? (
            <button onClick={finish}
                    disabled={saving}
                    className="btn-blood"
                    data-testid="tutorial-finish-button">
              {saving ? "MARKING..." : "RISE FROM THE DIRT"} <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                    className="btn-blood"
                    data-testid="tutorial-next-button">
              CONTINUE <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </main>
    </div>
  );
}


// ────────────── Practice Battle (Chapter VII) ──────────────
const APPROACHES = [
  { key: "cornfield", label: "CORNFIELD CRAWL",
    intel: "Slow. Hidden. Low casualties, fewer kills.",
    kill: 0.7, casualty: 0.4 },
  { key: "rooftop", label: "ROOFTOP DROP",
    intel: "Loud. Lethal. High kills, high casualties.",
    kill: 1.4, casualty: 1.3 },
  { key: "frontal", label: "FRONTAL CHARGE",
    intel: "No surprise. Brutal trade.",
    kill: 1.0, casualty: 1.0 },
];

const TARGETS = [
  { key: "civilian", label: "CIVILIANS",
    intel: "Convert at 75%. Easiest meat. No moral cost.",
    convert: 0.75 },
  { key: "soldier", label: "ARMED SOLDIERS",
    intel: "Convert at 50%. Coin flip. They shoot back.",
    convert: 0.50 },
  { key: "officer", label: "OFFICERS",
    intel: "Convert at 25%. Useless dead — but morale shatters.",
    convert: 0.25 },
];

const TACTICS = [
  { key: "mass", label: "USE THE MASS",
    intel: "Push your pack ahead. Standard. Reliable.",
    mult: 1.0 },
  { key: "lone", label: "LONE STRIKE",
    intel: "You go alone. High risk, high glory if you survive.",
    mult: 1.3 },
  { key: "personal_army", label: "SEND PERSONAL ARMY",
    intel: "Commit your own zombies for bigger kill counts.",
    mult: 1.15 },
];

function PracticeBattle() {
  const [approach, setApproach] = useState(null);
  const [target, setTarget] = useState(null);
  const [tactic, setTactic] = useState(null);
  const [outcome, setOutcome] = useState(null);

  const phase = !approach ? 1 : !target ? 2 : !tactic ? 3 : 4;

  const resolve = () => {
    // Mock formula: humans=10, kills = 10 × kill × mult, conversions = kills × convert
    const baseHumans = 10;
    const kills = Math.round(baseHumans * approach.kill * tactic.mult * 0.9);
    const converted = Math.round(kills * target.convert);
    const lost = Math.max(0, Math.round(kills * approach.casualty * 0.18));
    const moraleHit = target.key === "officer" ? "−12 (officers cracked)" :
                      target.key === "soldier" ? "−5" : "−2";
    setOutcome({ kills, converted, lost, moraleHit });
  };

  const reset = () => {
    setApproach(null);
    setTarget(null);
    setTactic(null);
    setOutcome(null);
  };

  return (
    <div className="mt-10 narrative-panel" data-testid="practice-battle">
      <div className="flex items-baseline justify-between mb-5">
        <p className="tac-label">PRACTICE DUEL // 10 HUMANS · OPEN FIELD</p>
        <p className="font-mono text-[0.65rem] text-muted-h">
          {phase < 4 ? `STEP ${phase} OF 3` : "RESOLVED"}
        </p>
      </div>

      {phase === 1 && (
        <PickerGrid label="PICK YOUR APPROACH" options={APPROACHES} onPick={setApproach} testidPrefix="practice-approach" />
      )}
      {phase === 2 && (
        <PickerGrid label="PICK YOUR TARGET" options={TARGETS} onPick={setTarget} testidPrefix="practice-target" />
      )}
      {phase === 3 && (
        <PickerGrid label="PICK YOUR TACTIC" options={TACTICS} onPick={setTactic} testidPrefix="practice-tactic" />
      )}

      {phase === 4 && !outcome && (
        <div className="mt-2 fade-up" data-testid="practice-summary">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <Choice label="APPROACH" value={approach.label} />
            <Choice label="TARGET" value={target.label} />
            <Choice label="TACTIC" value={tactic.label} />
          </div>
          <button onClick={resolve} className="btn-blood w-full" data-testid="practice-resolve-button">
            ENGAGE <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {outcome && (
        <div className="mt-2 fade-up" data-testid="practice-outcome">
          <div className="border-l-2 border-blood pl-4 py-2 mb-5">
            <p className="tac-label">OUTCOME</p>
            <p className="text-sm text-bone mt-2 leading-relaxed italic">
              You {approach.label.toLowerCase()} the field. The {target.label.toLowerCase()} fall.
              {" "}{outcome.kills} go down. {outcome.converted} rise beside you. {outcome.lost} of yours fall.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-xs font-mono">
              <Stat label="KILLED" value={outcome.kills} testid="practice-kills" />
              <Stat label="CONVERTED" value={outcome.converted} good testid="practice-converted" />
              <Stat label="YOU LOST" value={outcome.lost} bad testid="practice-lost" />
              <Stat label="MORALE" value={outcome.moraleHit} testid="practice-morale" />
            </div>
          </div>
          <p className="text-xs text-muted-h italic leading-relaxed mb-4">
            <span className="text-bone not-italic">Now you know.</span> Real battles do all of this and more —
            engagement levels, sally calls, AI-judged custom tactics. Pick wisely. The dead remember.
          </p>
          <button onClick={reset} className="btn-bone w-full" data-testid="practice-reset-button">
            <CheckCircle2 className="w-3 h-3" /> TRY ANOTHER COMBINATION
          </button>
        </div>
      )}
    </div>
  );
}

const PickerGrid = ({ label, options, onPick, testidPrefix }) => (
  <div className="fade-up">
    <p className="font-mono text-xs text-bone tracking-widest mb-3">{label}</p>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {options.map((o) => (
        <button
          key={o.key}
          onClick={() => onPick(o)}
          className="tactical-card text-left p-4 hover:border-blood hover:bg-[#0F0707] transition-all"
          data-testid={`${testidPrefix}-${o.key}`}
        >
          <p className="font-mono text-sm text-bone">{o.label}</p>
          <p className="text-[0.7rem] text-muted-h mt-2 leading-relaxed">{o.intel}</p>
        </button>
      ))}
    </div>
  </div>
);

const Choice = ({ label, value }) => (
  <div className="tactical-card">
    <p className="tac-label">{label}</p>
    <p className="font-mono text-sm text-bone mt-1.5 truncate">{value}</p>
  </div>
);

const Stat = ({ label, value, good, bad, testid }) => (
  <div className="tactical-card" data-testid={testid}>
    <p className="tac-label">{label}</p>
    <p className={`font-mono mt-1 ${good ? "text-[#7FBA9D]" : bad ? "text-blood" : "text-bone"}`}>
      {value}
    </p>
  </div>
);
